import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function FundingRegionAngel({
  data,
  fundraised,
  chartType = "default",
  totalDeals = null,
}) {
  const [chartOptions, setChartOptions] = useState(null);
  const isMobile = useIsMobile();

  // Define different color schemes for different chart instances
  const colorSchemes = {
    default: [
      "#EB760A", // Orange
      "#CF1B1B", // Red
      "#620AA8", // Purple
      "#224DBC", // Blue
      "#008000", // Green
      "#FFA500", // Orange
      "#4B0082", // Indigo
      "#B2AC88", // Beige
      "#adb8c2", // Light blue
    ],
    scheme1: [
      "#4ECDC4", // Teal (primary)
      "#45B7D1", // Light blue
      "#96CEB4", // Light green
      "#85C1E9", // Sky blue
      "#FFEAA7", // Light yellow
      "#DDA0DD", // Plum
      "#F8C471", // Light orange
      "#FF6B6B", // Light red
      "#F1C40F", // Yellow
    ],
    scheme2: [
      "#9B59B6", // Purple (primary)
      "#E67E22", // Dark orange
      "#2ECC71", // Green
      "#E74C3C", // Red
      "#3498DB", // Blue
      "#F39C12", // Orange
      "#1ABC9C", // Turquoise
      "#34495E", // Dark gray
      "#F1C40F", // Yellow
    ],
    scheme3: [
      "#16A085", // Teal
      "#E74C3C", // Red
      "#F39C12", // Orange
      "#8E44AD", // Purple
      "#2980B9", // Blue
      "#27AE60", // Green
      "#E67E22", // Dark orange
      "#34495E", // Dark gray
      "#F1C40F", // Yellow
    ],
  };

  // Function to get color scheme based on chart type/purpose
  const getColorScheme = (chartType) => {
    switch (chartType) {
      case "region":
        return colorSchemes.scheme1;
      case "funding-stage":
        return colorSchemes.scheme2;
      case "investment":
        return colorSchemes.scheme3;
      default:
        return colorSchemes.default;
    }
  };

  // Helper function to format percentages
  const formatPercentage = (value, total) => {
    const percentage = ((value / total) * 100).toFixed(1);
    return percentage.endsWith(".0") ? percentage.slice(0, -2) : percentage;
  };

  const initData = (data, chartType = "default") => {
    // Filter out items with value 0
    const filteredData = data.filter((item) => item.value > 0);

    // Calculate total and percentages
    const total = filteredData.reduce((acc, item) => acc + item.value, 0);

    // Get appropriate color scheme
    const colors = getColorScheme(chartType);

    const options = {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        width: isMobile ? 230 : 300,
        height: isMobile ? 230 : 300,
        style: {
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        },
      },
      title: false,
      plotOptions: {
        pie: {
          borderWidth: 0,
          minSize: 10,
          states: {
            hover: {
              halo: {
                size: 8,
                opacity: 0.3,
              },
            },
          },
        },
        series: {
          animation: {
            duration: 1000,
            easing: "easeOutBounce",
          },
          dataLabels: {
            enabled: false,
            style: {
              color: "#fff",
              textOutline: "none",
              fontSize: "11px",
              fontWeight: 500,
            },
          },
          shadow: {
            color: "rgba(0, 0, 0, 0.1)",
            offsetX: 0,
            offsetY: 2,
            opacity: 0.3,
            width: 4,
          },
        },
      },
      series: [
        {
          name: "",
          data: filteredData.map((item, index) => {
            const percentage = formatPercentage(item.value, total);
            return {
              name: item.region_name || item.name || item.stage_name,
              y: item.value,
              percentage: percentage,
              color: colors[index % colors.length],
              borderColor: colors[index % colors.length],
              borderWidth: 3,
              shadow: {
                color: colors[index % colors.length] + "40",
                offsetX: 0,
                offsetY: 2,
                opacity: 0.4,
                width: 6,
              },
            };
          }),
        },
      ],
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderColor: "#e2e8f0",
        borderRadius: 12,
        shadow: {
          color: "rgba(0, 0, 0, 0.1)",
          offsetX: 2,
          offsetY: 4,
          opacity: 0.3,
          width: 6,
        },
        style: {
          color: "#1a202c",
          fontSize: "13px",
          fontWeight: "500",
        },
        headerFormat: "",
        formatter: function () {
          const total = this.series.data.reduce(
            (acc, point) => acc + point.y,
            0
          );
          const percentage = formatPercentage(this.point.y, total);
          return `<div style="text-align: center;"><b>${this.point.name}</b><br/><span style="color:${this.point.color}">●</span> ${percentage}% (${this.point.y} deals)</div>`;
        },
        useHTML: true,
      },
    };

    return options;
  };

  useEffect(() => {
    // Filter out items with value 0
    let filteredData = data.filter((item) => item.value > 0);
    
    // If totalDeals is provided and there's a difference, add "Others" category
    if (totalDeals) {
      const currentTotal = filteredData.reduce((acc, item) => acc + item.value, 0);
      const difference = totalDeals - currentTotal;
      
      if (difference > 0) {
        filteredData = [...filteredData, {
          name: "Others",
          region_name: "Others",
          stage_name: "Others",
          value: difference
        }];
      }
    }
    
    // Sort data with "Others" always last
    const sortedData = filteredData.sort((a, b) => {
      const aIsOthers = (a.region_name === "Others" || a.name === "Others" || a.stage_name === "Others");
      const bIsOthers = (b.region_name === "Others" || b.name === "Others" || b.stage_name === "Others");
      
      if (aIsOthers && !bIsOthers) return 1; // "Others" goes to end
      if (!aIsOthers && bIsOthers) return -1; // Non-"Others" comes before
      
      return b.value - a.value; // Normal sorting by value for non-"Others" items
    });
    
    const opt = initData(sortedData, chartType);
    setChartOptions(opt);
  }, [data, chartType, totalDeals]);

  // Create the same processed data for legend
  let filteredDataForLegend = data.filter((item) => item.value > 0);
  
  if (totalDeals) {
    const currentTotal = filteredDataForLegend.reduce((acc, item) => acc + item.value, 0);
    const difference = totalDeals - currentTotal;
    
    if (difference > 0) {
      filteredDataForLegend = [...filteredDataForLegend, {
        name: "Others",
        region_name: "Others",
        stage_name: "Others",
        value: difference
      }];
    }
  }
  
  // Sort legend data with "Others" always last
  const sortedLegendData = filteredDataForLegend.sort((a, b) => {
    const aIsOthers = (a.region_name === "Others" || a.name === "Others" || a.stage_name === "Others");
    const bIsOthers = (b.region_name === "Others" || b.name === "Others" || b.stage_name === "Others");
    
    if (aIsOthers && !bIsOthers) return 1; // "Others" goes to end
    if (!aIsOthers && bIsOthers) return -1; // Non-"Others" comes before
    
    return b.value - a.value; // Normal sorting by value for non-"Others" items
  });

  // Calculate total for center display
  const total = totalDeals || filteredDataForLegend.reduce((acc, item) => acc + item.value, 0);

  // Get appropriate color scheme for legend
  const colors = getColorScheme(chartType);

  return (
    <div className='flex items-center justify-center w-full -my-5'>
      {data && (
        <div className='relative -ml-4 transition-all duration-300 transform lg:hover:scale-105 lg:ml-0'>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      )}

      <div className='w-1/4 lg:w-auto space-y-1.5 lg:space-y-2 text-[10px] py-4'>
        {sortedLegendData.map((item, index) => {
          return (
            <div
              className='flex items-center space-x-2.5 transition-all duration-200 rounded-lg group hover:bg-white/5'
              key={index}
            >
              <span
                className='shrink-0 transition-all duration-200 rounded-full size-2.5 lg:size-3 group-hover:scale-110 group-hover:shadow-lg'
                style={{
                  backgroundColor: colors[index % colors.length],
                  boxShadow: `0 0 0 2px ${colors[index % colors.length]}20`,
                }}
              ></span>
              <span className='font-medium transition-colors duration-200 text-white/90 group-hover:text-white'>
                {item.region_name || item.name || item.stage_name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}